/* Right-side search drawer.
   - Opened by the header's magnifier icon (".header__icon--search").
   - The input is debounced and, on every real change, fetches this
     section rendered in isolation for that query
     ("/search?q=...&section_id=search-drawer") -- Shopify's Section
     Rendering API re-runs sections/search-drawer.liquid server-side with a
     real populated "search" object, so title/SKU/product-code matching all
     come from Shopify's own search index for free. Only the
     "#SearchDrawer-ResultsInner" markup is pulled out of that response and
     swapped into the *live* drawer, so the input never loses focus/cursor
     position mid-request.
   - Reuses global.js's trapFocus/removeTrapFocus/debounce/fetchConfig
     helpers (loaded non-deferred in <head>, ready before this deferred
     script runs) and the same open/close class pattern as
     CartDrawer/QuickViewDrawer for consistency. */
(function () {
  class SearchDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('#SearchDrawer-Overlay');
      this.closeButton = this.querySelector('.search-drawer__close');
      this.input = this.querySelector('#SearchDrawer-Input');
      this.form = this.querySelector('#SearchDrawer-Form');
      this.resultsWrapper = this.querySelector('#SearchDrawer-Results');
      this.resultsInner = this.querySelector('#SearchDrawer-ResultsInner');
      this.openedBy = null;
      this.requestId = 0;

      this.overlay && this.overlay.addEventListener('click', this.close.bind(this));
      this.closeButton && this.closeButton.addEventListener('click', this.close.bind(this));
      this.addEventListener('keyup', (evt) => {
        if (evt.code === 'Escape') this.close();
      });

      if (this.form) {
        this.form.addEventListener('submit', (evt) => {
          evt.preventDefault();
          this.runSearch(this.input ? this.input.value : '');
        });
      }

      if (this.input) {
        const debouncedSearch = typeof debounce === 'function' ? debounce((value) => this.runSearch(value), 300) : (value) => this.runSearch(value);
        this.input.addEventListener('input', (evt) => debouncedSearch(evt.target.value));
      }

      this.querySelectorAll('[data-trending-term]').forEach((button) => {
        button.addEventListener('click', () => {
          const term = button.dataset.trendingTerm || '';
          if (this.input) this.input.value = term;
          this.runSearch(term);
          if (this.input) this.input.focus();
        });
      });
    }

    open(triggeredBy) {
      this.openedBy = triggeredBy || null;
      setTimeout(() => this.classList.add('active'));
      document.body.classList.add('overflow-hidden');
      const inner = this.querySelector('.drawer__inner');
      if (inner) trapFocus(inner, this.input || inner);
    }

    close() {
      this.classList.remove('active');
      removeTrapFocus(this.openedBy);
      document.body.classList.remove('overflow-hidden');
    }

    setState(hasQuery) {
      this.classList.toggle('has-query', hasQuery);
      this.classList.toggle('is-idle', !hasQuery);
    }

    async runSearch(rawValue) {
      const value = (rawValue || '').trim();

      if (value.length === 0) {
        this.setState(false);
        return;
      }

      this.setState(true);
      if (this.resultsWrapper) this.resultsWrapper.classList.add('is-loading');

      const thisRequest = ++this.requestId;
      const params = new URLSearchParams({
        q: value,
        type: 'product',
        'options[prefix]': 'last',
        'options[fields]': 'title,product_type,variants.title,variants.sku,tag',
        section_id: 'search-drawer'
      });

      try {
        const res = await fetch((window.routes && window.routes.search_url ? window.routes.search_url : '/search') + '?' + params.toString());
        if (thisRequest !== this.requestId) return; // a newer keystroke already superseded this request
        if (!res.ok) throw new Error('Search request failed');
        const html = await res.text();
        if (thisRequest !== this.requestId) return;

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshInner = doc.getElementById('SearchDrawer-ResultsInner');
        if (freshInner && this.resultsInner) {
          this.resultsInner.innerHTML = freshInner.innerHTML;
          if (typeof window.initProductCardSwipers === 'function') window.initProductCardSwipers();
          if (typeof window.initQuickViewTriggers === 'function') window.initQuickViewTriggers();
        }
      } catch (err) {
        if (thisRequest === this.requestId && this.resultsInner) {
          this.resultsInner.innerHTML = '<p class="search-drawer__no-results">' + ((window.searchDrawerStrings && window.searchDrawerStrings.error) || 'Something went wrong. Please try again.') + '</p>';
        }
      } finally {
        if (thisRequest === this.requestId && this.resultsWrapper) {
          this.resultsWrapper.classList.remove('is-loading');
        }
      }
    }
  }

  if (!customElements.get('search-drawer')) {
    customElements.define('search-drawer', SearchDrawer);
  }

  function initTrigger() {
    document.querySelectorAll('.header__icon--search').forEach((trigger) => {
      if (trigger.dataset.searchBound) return;
      trigger.dataset.searchBound = 'true';
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        const drawer = document.querySelector('search-drawer');
        if (!drawer) return;
        drawer.open(trigger);
        const input = drawer.querySelector('#SearchDrawer-Input');
        if (input) setTimeout(() => input.focus(), 100);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTrigger);
  } else {
    initTrigger();
  }
  document.addEventListener('shopify:section:load', initTrigger);
})();
